import Dexie, { type Table } from 'dexie';
import type { SyncMetadata } from '../db';
import {
  createSyncMetadata,
  updateSyncMetadata,
  softDeleteSyncMetadata,
} from '../sync/syncMetadata';
import { queueForSync } from '../sync/syncOutbox';

/**
 * Minimal CRUD contract for repositories.
 *
 * Notes:
 * - This is intentionally small and generic: repos may expose richer APIs (e.g. listByCompany, upsert, etc.).
 * - IDs are string-based to match the IndexedDB/Dexie schema in this app.
 */
interface BaseRepo<TListModel, TGetModel, TInput, TId extends string = string> {
  list(): Promise<TListModel[]>;
  get(id: TId): Promise<TGetModel | null>;
  save(input: TInput): Promise<TId>;
  delete(id: TId): Promise<void>;
}

/**
 * Abstract base to enable shared Dexie CRUD implementations.
 *
 * Concrete repos will provide:
 * - The Dexie table
 * - Row <-> model mapping
 * - Row construction logic for save/upsert
 */
export abstract class AbstractBaseRepo<
  TListModel,
  TGetModel,
  TRow,
  TInput,
  TId extends string = string,
> implements BaseRepo<TListModel, TGetModel, TInput, TId> {
  protected abstract readonly table: Table<TRow, TId>;

  protected abstract getRowId(row: TRow): TId;
  protected abstract toListModel(row: TRow): TListModel | Promise<TListModel>;
  protected abstract toGetModel(row: TRow): TGetModel | Promise<TGetModel>;
  protected abstract toRow(
    input: TInput,
    ctx: { now: string; existing?: TRow }
  ): TRow | Promise<TRow>;

  /**
   * Optional: extract the primary key from the input so the base implementation
   * can load an existing row and pass it to `toRow(...)` for upsert cases.
   */
  protected getInputId(_input: TInput): TId | null {
    return null;
  }

  protected now(): string {
    return new Date().toISOString();
  }

  /**
   * Check if a row is soft-deleted.
   */
  protected isDeleted(row: TRow): boolean {
    if (!this.useSyncMetadata) return false;
    const syncRow = row as unknown as Partial<SyncMetadata>;
    return !!syncRow.syncDeletedAt;
  }

  async list(): Promise<TListModel[]> {
    const rows = await this.table.toArray();
    const activeRows = this.useSyncMetadata ? rows.filter((row) => !this.isDeleted(row)) : rows;
    return Promise.all(activeRows.map((row) => this.toListModel(row)));
  }

  /**
   * List all records including soft-deleted ones (for sync purposes).
   */
  async listAll(): Promise<TListModel[]> {
    const rows = await this.table.toArray();
    return Promise.all(rows.map((row) => this.toListModel(row)));
  }

  async get(id: TId): Promise<TGetModel | null> {
    const row = await this.table.get(id);
    if (!row) return null;
    // Return soft-deleted records (caller can check deletedAt if needed)
    return await this.toGetModel(row);
  }

  /**
   * Whether this repo uses sync metadata (soft deletes, versioning).
   * Override to false for device-local tables like kv.
   */
  protected readonly useSyncMetadata: boolean = true;

  protected getOutboxTableName(): string {
    return this.table.name;
  }

  protected async serializeOutboxPayload(row: TRow, _input?: TInput): Promise<unknown> {
    return row;
  }

  async save(input: TInput): Promise<TId> {
    const now = this.now();
    const existingId = this.getInputId(input);
    const existing = existingId ? await this.table.get(existingId) : undefined;
    let row = await this.toRow(input, { now, existing });

    // Add sync metadata if this repo uses it
    if (this.useSyncMetadata) {
      const syncMeta = existing
        ? updateSyncMetadata(existing as unknown as SyncMetadata)
        : createSyncMetadata();
      row = { ...row, ...syncMeta };
    }

    const id = this.getRowId(row);
    await this.table.put(row);

    if (this.useSyncMetadata) {
      const payload = await this.serializeOutboxPayload(row, input);
      const tableName = this.getOutboxTableName();
      const recordId = String(id);
      const activeTx = Dexie.currentTransaction;

      if (activeTx) {
        activeTx.on('complete', () => {
          void queueForSync(tableName, recordId, 'upsert', payload);
        });
      } else {
        await queueForSync(tableName, recordId, 'upsert', payload);
      }
    }

    return id;
  }

  /**
   * Hard delete - removes record from local database.
   * For synced tables, prefer softDelete() to preserve tombstone for sync.
   */
  async delete(id: TId): Promise<void> {
    await this.table.delete(id);
  }

  /**
   * Soft delete - marks record as deleted without removing it.
   * The sync engine will propagate the deletion to other devices.
   */
  async softDelete(id: TId): Promise<void> {
    if (!this.useSyncMetadata) {
      // Fall back to hard delete for non-synced tables
      await this.delete(id);
      return;
    }

    const existing = await this.table.get(id);
    if (!existing) return;

    const syncMeta = softDeleteSyncMetadata(existing as unknown as SyncMetadata);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await this.table.update(id, syncMeta as any);

    const payload = await this.serializeOutboxPayload({
      ...(existing as object),
      ...syncMeta,
    } as TRow);
    const tableName = this.getOutboxTableName();
    const recordId = String(id);
    const activeTx = Dexie.currentTransaction;

    if (activeTx) {
      activeTx.on('complete', () => {
        void queueForSync(tableName, recordId, 'delete', payload);
      });
    } else {
      await queueForSync(tableName, recordId, 'delete', payload);
    }
  }
}
